import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../../environments/enviroment';
import { Agent } from '../../../../models/users';

export interface CreateAgentPayload {
  email: string;
  username: string;
  version: string;
  ip: string;
  workspace_id: string;
  password: string;
  status: string;
  role: 'agent';
  campaigns: string[];
}

export interface UpdateAgentPayload {
  email?: string;
  username?: string;
  version?: string;
  ip?: string;
  workspace_id?: string;
  status?: string;
  role?: 'agent';
  campaigns?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AgentService {
  private agentListUrl = `${environment.mindxbkUrl}Agent`;
  private msAgentUrl = `${environment.mindxbkUrl}Agent`;

  constructor(
    private http: HttpClient
  ) {
  }

  private buildHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      throw new Error('No auth token available');
    }

    return new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    });
  }

  getAgentsByRole(role: string): Observable<Agent[]> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const url = `${this.agentListUrl}/list_agent?roles=${role}`;
    return this.http.get<Agent[]>(url, { headers });
  }

  assignCampaignAgent(email: string, campaignId: string): Observable<any> {
    const workspaceId = this.resolveWorkspaceId();
    if (!workspaceId) {
      return throwError(() => new Error('No workspace available'));
    }

    return this.assignCampaignAgentByWorkspace(email, campaignId, workspaceId);
  }

  assignCampaignAgentByWorkspace(email: string, campaignId: string, workspaceId: string): Observable<any> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const url = `${this.agentListUrl}/assign-campaign?email=${encodeURIComponent(email)}&workspace_id=${encodeURIComponent(workspaceId)}&campaign_id=${encodeURIComponent(campaignId)}`;
    return this.http.post<any>(url, {}, { headers });
  }

  getAgentsByCampaign(campaignId: string): Observable<Agent[]> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const url = `${this.agentListUrl}/agents-by-campaign/${campaignId}`;
    return this.http.get<Agent[]>(url, { headers });
  }

  getAgentsByWorkspace(workspaceId: string, role: string): Observable<Agent[]> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const base = this.agentListUrl.replace(/\/$/, '');
    const url = `${base}/agents_by_workspace?workspace_id=${encodeURIComponent(workspaceId)}&rol=${role}`;

    return this.http.get<Agent[]>(url, { headers });
  }

  getAgentByEmailAndWorkspace(email: string, workspaceId: string): Observable<Agent> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const url = `${this.agentListUrl}/get_by_email?email=${encodeURIComponent(email)}&workspace_id=${encodeURIComponent(workspaceId)}`;
    return this.http.get<Agent>(url, { headers });
  }

  createAgent(payload: CreateAgentPayload): Observable<Agent> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    return this.http.post<Agent>(`${this.msAgentUrl}/create`, payload, { headers });
  }

  updateAgent(agentId: string, payload: UpdateAgentPayload): Observable<Agent> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    return this.http.patch<Agent>(`${this.msAgentUrl}/update/${agentId}`, payload, { headers });
  }

  removeCampaignFromAgent(email: string, campaignId: string): Observable<any> {
    const workspaceId = this.resolveWorkspaceId();
    if (!workspaceId) {
      return throwError(() => new Error('No workspace available'));
    }

    return this.removeCampaignFromAgentByWorkspace(email, campaignId, workspaceId);
  }

  removeCampaignFromAgentByWorkspace(email: string, campaignId: string, workspaceId: string): Observable<any> {
    let headers: HttpHeaders;
    try {
      headers = this.buildHeaders();
    } catch (error) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => error instanceof Error ? error : new Error('No auth token available'));
    }

    const url = `${this.agentListUrl}/remove-campaign-agent?email=${encodeURIComponent(email)}&workspace_id=${encodeURIComponent(workspaceId)}&campaign_id=${encodeURIComponent(campaignId)}`;
    return this.http.delete<any>(url, { headers });
  }

  setAgentConversation(data: any, token: string): Observable<Object> {
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(
      `${environment.mindxbkUrl}CustomerConversation/Conversation/AgentAssignedToConversation`,
      data,
      { headers }
    );
  }

  isConversationAlreadySelected(conversationId: any, token: string): Observable<Object> {
    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(
      `${environment.mindxbkUrl}CustomerConversation/Conversation/${conversationId}`,
      { headers }
    );
  }

  private resolveWorkspaceId(): string {
    const raw = sessionStorage.getItem('workspace')
      || sessionStorage.getItem('workpsaceId')
      || sessionStorage.getItem('workspaceId')
      || localStorage.getItem('workspace')
      || localStorage.getItem('workpsaceId')
      || localStorage.getItem('workspaceId');

    if (!raw) {
      return '';
    }

    try {
      const workspace = JSON.parse(raw);
      return String(workspace?.id || workspace?.workspaceId || '').trim();
    } catch {
      return '';
    }
  }
}
